import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, Typography, Divider } from '@mui/material'
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { withStyles } from '@mui/styles'

import { expansionPanelStyles } from './expansion-panel.styles'

/**
 * @typedef {object} ExpansionPanelProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {React.ReactNode} summary - El contenido que se muestra siempre en la parte superior del panel.
 * @property {React.ReactNode} [details] - El contenido expandible que se muestra/oculta al hacer clic en el trigger. Si no se proporciona, el panel no será expandible.
 * @property {React.ReactNode} [summaryExtra] - Contenido adicional que se muestra en el área del resumen, antes del trigger de expansión.
 * @property {React.ReactNode} [footer] - Contenido que se muestra debajo del accordion, fuera del área expandible.
 * @property {'left' | 'right' | 'center'} [triggerPosition='left'] - La posición horizontal del botón de expansión/colapso.
 * @property {boolean} [isExpanded=false] - Estado inicial de expansión del panel.
 * @property {boolean} [inactive] - Si es `true`, deshabilita la interacción con el panel (el cursor no cambia al pasar sobre él).
 * @property {function(boolean): void | false} [onExpand=false] - Callback que se ejecuta cuando el estado de expansión cambia. Recibe el nuevo estado como parámetro.
 */

/**
 * Un componente de panel de expansión personalizable basado en Accordion de Material-UI.
 * 
 * Este componente proporciona un contenedor expandible/colapsable con las siguientes características:
 * - **Resumen siempre visible**: Contenido que permanece visible independientemente del estado de expansión
 * - **Detalles expandibles**: Contenido que se muestra/oculta mediante un trigger
 * - **Contenido extra en resumen**: Área adicional para botones, badges u otro contenido en el resumen
 * - **Footer opcional**: Contenido que se muestra fuera del área expandible
 * - **Control de posición del trigger**: Permite posicionar el botón de expansión a la izquierda, derecha o centro
 * - **Internacionalización**: Textos de expansión/colapso traducidos automáticamente
 * 
 * El panel solo es interactivo si se proporciona la prop `details`. Sin ella, funciona como un contenedor estático.
 * 
 * @component
 * @example
 * const MyExpansionPanel = () => {
 *   const [expanded, setExpanded] = useState(false);
 * 
 *   return (
 *     <ExpansionPanelComponent
 *       summary={
 *         <Box>
 *           <Typography variant="h6">Información del Proyecto</Typography>
 *           <Typography variant="body2">Proyecto Alpha - En progreso</Typography>
 *         </Box>
 *       }
 *       summaryExtra={
 *         <Chip label="3 tareas pendientes" color="warning" />
 *       }
 *       details={
 *         <Box>
 *           <Typography>Detalles completos del proyecto...</Typography>
 *           <List>
 *             <ListItem>Tarea 1</ListItem>
 *             <ListItem>Tarea 2</ListItem>
 *           </List>
 *         </Box>
 *       }
 *       footer={
 *         <Button variant="outlined">Ver más detalles</Button>
 *       }
 *       isExpanded={expanded}
 *       onExpand={(newState) => setExpanded(newState)}
 *       triggerPosition="right"
 *     />
 *   );
 * };
 * 
 * @param {ExpansionPanelProps} props - Las props del componente ExpansionPanelComponent.
 */
export const ExpansionPanelComponent = withStyles(expansionPanelStyles)(({
  classes,
  triggerPosition = 'left',
  isExpanded = false,
  inactive,
  onExpand = false,
  ...props
}) => {
  const { formatMessage } = useIntl()

  const [expanded, setExpanded] = useState(isExpanded)

  const handleExpand = () => {
    const newState = !expanded
    props.details && setExpanded(newState)
    if (typeof onExpand === 'function') onExpand(newState)
  }

  return (
    <>
      <Accordion className={classes.accordion} expanded={expanded}>
        <AccordionSummary
          className={classes.accordionSummary}
          style={inactive ? { cursor: 'default' } : {}}
        >
          <Box mb={2}>{props.summary}</Box>
          {props.details && (
            <>
              <Divider light />
              <Box mt={2} display="flex" alignItems="center" flexWrap="wrap">
                {props.summaryExtra && <Box flexGrow={1}>{props.summaryExtra}</Box>}
                <Box flexGrow={1} textAlign={triggerPosition} onClick={handleExpand}>
                  <Typography variant="body2" color="primary" className={classes.accordionToggler}>
                    {formatMessage({
                      id: `pages.budget.view.panel1.summary.expanded.${expanded.toString()}`,
                    })}
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </AccordionSummary>
        <AccordionDetails>{props.details}</AccordionDetails>
      </Accordion>
      {props.footer && (
        <Box ml={2} mr={2}>
          {props.footer}
        </Box>
      )}
    </>
  )
})
